import { useRef, useState } from 'react';
import './PhotoCapture.css';

interface PhotoCaptureProps {
  onImageSelect: (file: File) => void;
}

export default function PhotoCapture({ onImageSelect }: PhotoCaptureProps) {
  const [showOptions, setShowOptions] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('Image is too large. Please select an image under 10MB.');
        return;
      }

      onImageSelect(file);
    }
    setShowOptions(false);

    // Reset the input so the same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const openCamera = () => {
    cameraInputRef.current?.click();
    setShowOptions(false);
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
    setShowOptions(false);
  };

  return (
    <div className="photo-capture">
      <button
        type="button"
        className="photo-btn"
        onClick={() => setShowOptions(!showOptions)}
        aria-label="Add photo"
      >
        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
          <path d="M12 12c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6-1.8V6H6v4.2c-.6.3-1 .9-1 1.6V18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2v-6.2c0-.7-.4-1.3-1-1.6zM9 8h6v2H9V8zm3 10c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4z"/>
        </svg>
      </button>

      {showOptions && (
        <>
          <div className="photo-options-overlay" onClick={() => setShowOptions(false)} />
          <div className="photo-options">
            <button type="button" onClick={openCamera} className="photo-option">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M12 10.8c-1.8 0-3.2 1.4-3.2 3.2s1.4 3.2 3.2 3.2 3.2-1.4 3.2-3.2-1.4-3.2-3.2-3.2zM21 6h-3.2l-1.9-2H8.1L6.2 6H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 14H3V8h4.9l1.9-2h4.4l1.9 2H21v12z"/>
              </svg>
              Take Photo
            </button>
            <button type="button" onClick={openFilePicker} className="photo-option">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-4.86 8.86l-3 3.87L9 13.14 6 17h12l-3.86-5.14z"/>
              </svg>
              Choose Photo
            </button>
          </div>
        </>
      )}

      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
    </div>
  );
}
